import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, ArrowUpRight, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatIDR } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const toCartItem = (product, variant) => {
  const v = variant || (product.variants && product.variants.length ? product.variants[0] : null);
  if (!v) return product;
  return {
    ...product,
    id: `${product.id}::${v.name}`,
    name: `${product.name} · ${v.name}`,
    price: v.price,
  };
};

const ProductCard = ({ product, index, onOpen }) => {
  const { addItem } = useCart();
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: (index % 3) * 0.08 }}
      data-testid={`product-card-${product.id}`}
      className="group relative flex flex-col clip-frame rounded-2xl bg-[#0f0f0f] overflow-hidden"
    >
      <div className="relative aspect-[4/5] overflow-hidden cursor-pointer" onClick={() => onOpen(product)}>
        <motion.img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover"
          whileHover={{ scale: 1.06 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60 pointer-events-none" />
        {product.badge && (
          <span className="absolute top-4 left-4 bg-accent-wl text-black text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
            {product.badge}
          </span>
        )}
        <span className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/50 backdrop-blur border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowUpRight className="w-4 h-4 text-white" />
        </span>
      </div>

      <div className="flex flex-col flex-1 p-5">
        <p className="text-[11px] uppercase tracking-[0.2em] text-accent-wl mb-1">{product.brand}</p>
        <h3 className="font-display font-bold text-lg leading-tight text-white">{product.name}</h3>
        <p className="font-body text-sm text-white/40 mt-1 line-clamp-1">{product.tagline}</p>

        <div className="mt-4 flex items-end justify-between gap-2">
          <div>
            {product.original_price && (
              <span className="block text-xs text-white/30 line-through">{formatIDR(product.original_price)}</span>
            )}
            {product.variants && product.variants.length > 1 && (
              <span className="block text-[10px] uppercase tracking-wider text-white/40">Mulai</span>
            )}
            <span className="font-display font-bold text-white text-lg">{formatIDR(product.price)}</span>
          </div>
          <button
            onClick={() => addItem(toCartItem(product))}
            data-testid="add-to-cart-btn"
            className="w-11 h-11 shrink-0 rounded-full bg-white text-black flex items-center justify-center hover:bg-accent-wl transition-colors"
            aria-label="Tambah ke keranjang"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const Catalog = () => {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [active, setActive] = useState("All");
  const [selected, setSelected] = useState(null);
  const [variant, setVariant] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    setVariant(selected?.variants?.length ? selected.variants[0] : null);
  }, [selected]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data.brands)).catch(() => {});
  }, []);

  const filtered = active === "All" ? products : products.filter((p) => p.brand === active);

  return (
    <section id="katalog" className="relative bg-[#050505] py-24 lg:py-36">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div>
            <span className="font-body text-xs uppercase tracking-[0.3em] text-gradient-wl font-semibold">Katalog Pilihan</span>
            <h2 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-3 leading-none">
              Gadget yang <br /> layak kamu miliki.
            </h2>
          </div>
          <p className="font-body text-white/50 max-w-sm">
            Semua unit original, tersegel, dan bergaransi resmi. Pilih brand favoritmu di bawah ini.
          </p>
        </div>

        {/* Brand filter */}
        <div className="flex flex-wrap gap-3 mb-12" data-testid="brand-filter">
          {["All", ...brands].map((b) => (
            <button
              key={b}
              onClick={() => setActive(b)}
              data-testid={`filter-${b.toLowerCase()}`}
              className={`px-5 py-2 rounded-full font-body text-sm border transition-colors duration-200 ${
                active === b
                  ? "bg-white text-black border-white"
                  : "bg-transparent text-white/60 border-white/15 hover:border-white/40 hover:text-white"
              }`}
            >
              {b === "All" ? "Semua" : b}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} onOpen={setSelected} />
          ))}
        </div>
      </div>

      {/* Product detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto bg-[#0a0a0a] border border-white/10 text-white p-0 no-scrollbar" data-testid="product-detail">
          {selected && (
            <div className="grid md:grid-cols-2">
              <DialogTitle className="sr-only">{selected.name}</DialogTitle>
              <div className="relative aspect-square md:aspect-auto md:h-full bg-black">
                <img src={selected.image} alt={selected.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-8 flex flex-col">
                <p className="text-xs uppercase tracking-[0.25em] text-accent-wl">{selected.brand} · {selected.category}</p>
                <h3 className="font-display font-black text-3xl tracking-tight mt-2">{selected.name}</h3>
                <p className="font-body italic text-white/50 mt-1">{selected.tagline}</p>
                <p className="font-body text-sm text-white/70 mt-4 leading-relaxed">{selected.description}</p>

                <ul className="mt-5 space-y-2">
                  {selected.specs?.map((s, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-white/70">
                      <Check className="w-4 h-4 text-accent-wl shrink-0" /> {s}
                    </li>
                  ))}
                </ul>

                {selected.variants && selected.variants.length > 0 && (
                  <div className="mt-6" data-testid="variant-selector">
                    <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Pilih Memori / Varian</p>
                    <div className="flex flex-wrap gap-2">
                      {selected.variants.map((v) => (
                        <button
                          key={v.name}
                          onClick={() => setVariant(v)}
                          data-testid={`variant-${v.name.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}`}
                          className={`px-4 py-2 rounded-full font-body text-sm border transition-colors duration-200 ${
                            variant?.name === v.name
                              ? "bg-white text-black border-white"
                              : "bg-transparent text-white/70 border-white/20 hover:border-white/50"
                          }`}
                        >
                          {v.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-auto pt-6">
                  <span className="font-display font-bold text-3xl block mb-4" data-testid="detail-price">
                    {formatIDR(variant ? variant.price : selected.price)}
                  </span>
                  <Button
                    onClick={() => { addItem(toCartItem(selected, variant)); setSelected(null); }}
                    className="w-full bg-gradient-wl text-white hover:opacity-90 font-body font-semibold rounded-full h-12 transition-opacity"
                    data-testid="detail-add-to-cart"
                  >
                    Tambah ke Keranjang
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Catalog;
